import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { usePortfolio } from "@/context/PortfolioContext";
import { EmptyState } from "@/components/EmptyState";
import { PerformanceChart, RangeSelector, AllocationDonut, AllocationLegend } from "@/components/Charts";
import { SubPortfolioSelect } from "@/components/SubPortfolioSelect";
import { TransactionDialog } from "@/components/TransactionDialog";
import { MoversCard, RecentTransactionsCard } from "@/components/dashboard/DashboardCards";
import { PortfolioStrip } from "@/components/dashboard/PortfolioStrip";
import { MarketLists } from "@/components/dashboard/MarketLists";
import { SubPortfolioHeatmap } from "@/components/dashboard/SubPortfolioHeatmap";
import { usePrivacy } from "@/context/PrivacyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtCurrency, fmtSigned, fmtPct, fmtDate, gainClass } from "@/lib/format";
import { Plus, PieChart, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
    const { selectedSub, setSelectedSub, refreshSubs } = usePortfolio();
    const { mask } = usePrivacy();
    const [range, setRange] = useState("1W");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [moverMode, setMoverMode] = useState("moves");
    const [moverTf, setMoverTf] = useState("1D");
    const [moverCount, setMoverCount] = useState(10);
    const [moverSub, setMoverSub] = useState("ALL");
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [refreshing, setRefreshing] = useState(false);

    const refreshMarket = async () => {
        if (refreshing) return;
        setRefreshing(true);
        toast.info("Refreshing market data…");
        try {
            await api.post("/market/refresh");
            await qc.invalidateQueries();
            toast.success("Data refreshed", { duration: 1500 });
        } catch {
            toast.error("Refresh failed");
        } finally {
            setTimeout(() => setRefreshing(false), 400);
        }
    };

    const summary = useQuery({ queryKey: ["summary", selectedSub], queryFn: async () => (await api.get(`/portfolio/summary?sub=${selectedSub}`)).data });
    const perf = useQuery({ queryKey: ["perf", selectedSub, range], queryFn: async () => (await api.get(`/portfolio/performance?range=${range}&sub=${selectedSub}`)).data });
    const holdings = useQuery({ queryKey: ["holdings", selectedSub], queryFn: async () => (await api.get(`/portfolio/holdings?sub=${selectedSub}`)).data });
    const alloc = useQuery({ queryKey: ["alloc", selectedSub], queryFn: async () => (await api.get(`/portfolio/allocation?sub=${selectedSub}`)).data });
    const txns = useQuery({ queryKey: ["recent-txns", selectedSub], queryFn: async () => (await api.get(`/transactions?sub_portfolio=${selectedSub}`)).data });
    const markets = useQuery({ queryKey: ["markets"], queryFn: async () => (await api.get("/market/metrics")).data, refetchInterval: 60000 });
    const ytd = useQuery({ queryKey: ["dash-ytd", selectedSub], queryFn: async () => (await api.get(`/portfolio/performance?range=YTD&sub=${selectedSub}`)).data });
    const movers = useQuery({ queryKey: ["top-movers", moverMode, moverTf, moverCount, moverSub], queryFn: async () => (await api.get(`/portfolio/top-movers?limit=${moverCount}&mode=${moverMode}&timeframe=${moverTf}&sub_portfolio=${encodeURIComponent(moverSub)}`)).data });

    const refetchAll = () => qc.invalidateQueries();
    const s = summary.data;
    const ytdM = ytd.data?.metrics;
    const empty = summary.isSuccess && s?.holdings_count === 0 && (txns.data?.length ?? 0) === 0;

    if (summary.isError) {
        const e = summary.error;
        const status = e?.response?.status;
        const dataBody = e?.response?.data;
        const bodyStr = typeof dataBody === "string" ? dataBody : dataBody ? JSON.stringify(dataBody) : "";
        const diag = [status ? `HTTP ${status}` : (e?.code || "no response"), e?.message, bodyStr].filter(Boolean).join(" · ");
        return (
            <Card className="mt-4 p-8 text-center space-y-3" data-testid="dashboard-summary-error">
                <h2 className="font-heading text-lg font-semibold">Couldn't load your portfolio</h2>
                <p className="text-sm text-muted-foreground">We hit a problem fetching your portfolio summary. This is usually temporary.</p>
                <pre className="text-left text-[11px] text-muted-foreground bg-muted/50 rounded-lg p-3 overflow-auto max-h-40 whitespace-pre-wrap" data-testid="dashboard-summary-error-detail">{diag}</pre>
                <Button onClick={() => { summary.refetch(); refetchAll(); }} data-testid="dashboard-summary-retry"><Plus className="w-4 h-4 mr-1.5 rotate-45" />Retry</Button>
            </Card>
        );
    }

    if (empty) {
        return (
            <>
                <Card className="mt-4">
                    <EmptyState icon={PieChart} title="Your portfolio is empty"
                        description="Add your first transaction to start tracking holdings, performance and allocation."
                        actionLabel="Add your first transaction" onAction={() => setDialogOpen(true)} testId="dashboard-empty" />
                </Card>
                <TransactionDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={() => { refreshSubs(); refetchAll(); }} />
            </>
        );
    }

    // group holdings by sub-portfolio, sorted by value desc
    const grouped = {};
    (holdings.data?.holdings || []).forEach((h) => { (grouped[h.sub_portfolio] ||= []).push(h); });
    Object.values(grouped).forEach((arr) => arr.sort((a, b) => (b.market_value || 0) - (a.market_value || 0)));
    const moverRows = movers.data?.movers || [];

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-heading text-2xl font-bold tracking-tight">Dashboard</h1>
                    <p className="text-sm text-muted-foreground">{s ? `As of ${fmtDate(s.as_of)} · ${s.source} · updated ${new Date(summary.dataUpdatedAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}` : "Loading…"}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <SubPortfolioSelect value={selectedSub} onChange={setSelectedSub} />
                    <Button variant="outline" size="icon" onClick={refreshMarket} disabled={refreshing} title="Refresh market data" data-testid="dashboard-refresh-btn">
                        <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                    </Button>
                    <Button onClick={() => setDialogOpen(true)} data-testid="add-transaction-btn"><Plus className="w-4 h-4 mr-1.5" />Add Transaction</Button>
                </div>
            </div>

            {/* Headline figures */}
            {(summary.isLoading || !s) ? <Skeleton className="h-[64px] rounded-xl" /> : (
                <PortfolioStrip cells={[
                    { testId: "stat-portfolio-value", label: "Portfolio Value", value: mask(fmtCurrency(s.portfolio_value)), delta: s.day_change, deltaPct: fmtPct(s.day_change_pct) },
                    { testId: "stat-ytd-return", label: "YTD Return", value: ytdM ? mask(fmtSigned(ytdM.abs_change)) : "—", valueClass: gainClass(ytdM?.abs_change), delta: ytdM?.abs_change, deltaPct: ytdM ? fmtPct(ytdM.pct_change) : "" },
                    { testId: "stat-today", label: "Today", value: mask(fmtSigned(s.day_change)), valueClass: gainClass(s.day_change), delta: s.day_change, deltaPct: fmtPct(s.day_change_pct) },
                    { testId: "stat-invested", label: "Invested", value: mask(fmtCurrency(s.invested)), sub: `${s.holdings_count} positions` },
                    { testId: "stat-cash", label: "Cash", value: mask(fmtCurrency(s.cash)), sub: "available" },
                ]} />
            )}

            {markets.data && <MarketLists data={markets.data} onOpen={(k) => navigate(`/metric/${k}`)} />}

            {/* Chart + sub-portfolio heatmap */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <Card className="lg:col-span-2 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <h2 className="font-heading text-base font-semibold">Portfolio Value</h2>
                        <RangeSelector value={range} onChange={setRange} />
                    </div>
                    {perf.isLoading ? <Skeleton className="h-[200px] rounded-lg" /> : <PerformanceChart series={perf.data?.series || []} height={200} />}
                </Card>
                <SubPortfolioHeatmap sub={selectedSub} mask={mask} height={210} />
            </div>

            {/* Movers + recent txns */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <MoversCard mode={moverMode} setMode={setMoverMode} tf={moverTf} setTf={setMoverTf}
                    count={moverCount} setCount={setMoverCount} sub={moverSub} setSub={setMoverSub}
                    rows={moverRows} loading={movers.isLoading} mask={mask} onNavigate={navigate} />
                <RecentTransactionsCard txns={txns.data} mask={mask} onViewAll={() => navigate("/transactions")} />
            </div>

            {/* Allocation */}
            <Card className="p-4" data-testid="dashboard-allocation-card">
                <h2 className="font-heading text-base font-semibold">Allocation <span className="text-xs font-normal text-muted-foreground">· by sub-portfolio</span></h2>
                {alloc.isLoading ? <Skeleton className="h-[150px] rounded-lg mt-2" /> : (
                    <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] items-center gap-4 mt-1">
                        <div data-testid="dashboard-allocation-chart"><AllocationDonut data={alloc.data?.by_sub_portfolio || []} height={150} /></div>
                        <AllocationLegend data={alloc.data?.by_sub_portfolio || []} />
                    </div>
                )}
            </Card>

            <TransactionDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={() => { refreshSubs(); refetchAll(); }} />
        </div>
    );
}
